/**
 * Pagination Component
 *
 * Displays page navigation with ellipsis for large page counts.
 * Apple-like minimalist design with clean button states.
 *
 * @module components/Pagination
 */

import { View, ViewStyle, TextStyle, Pressable } from "react-native"

import { colors, spacing } from "@/theme"

import { Text } from "./Text"

export interface PaginationProps {
  /**
   * Current active page (1-indexed)
   */
  currentPage: number

  /**
   * Total number of pages
   */
  totalPages: number

  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void

  /**
   * Optional style override
   */
  style?: ViewStyle
}

/**
 * Pagination Component
 *
 * Shows: < Zurück | 1 | 2 | 3 | ... | 10 | Weiter >
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={2}
 *   totalPages={10}
 *   onPageChange={(page) => console.log(page)}
 * />
 * ```
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  style: $styleOverride,
}: PaginationProps) {
  // Generate page numbers with ellipsis
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = []

    // Always show first page
    pages.push(1)

    // Calculate range around current page
    const rangeStart = Math.max(2, currentPage - 1)
    const rangeEnd = Math.min(totalPages - 1, currentPage + 1)

    // Add ellipsis after first page if needed
    if (rangeStart > 2) {
      pages.push("...")
    }

    // Add pages in range
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i)
    }

    // Add ellipsis before last page if needed
    if (rangeEnd < totalPages - 1) {
      pages.push("...")
    }

    // Always show last page (if more than 1 page)
    if (totalPages > 1) {
      pages.push(totalPages)
    }

    return pages
  }

  const pages = getPageNumbers()
  const canGoPrevious = currentPage > 1
  const canGoNext = currentPage < totalPages

  return (
    <View style={[$container, $styleOverride]}>
      {/* Previous Button */}
      <Pressable
        onPress={() => canGoPrevious && onPageChange(currentPage - 1)}
        disabled={!canGoPrevious}
        style={({ pressed }) => [
          $navigationButton,
          !canGoPrevious && $navigationButtonDisabled,
          pressed && canGoPrevious && $navigationButtonPressed,
        ]}
      >
        <Text style={[$navigationButtonText, !canGoPrevious && $navigationButtonTextDisabled]}>
          Zurück
        </Text>
      </Pressable>

      {/* Page Numbers */}
      <View style={$pagesContainer}>
        {pages.map((page, index) => {
          if (page === "...") {
            return (
              <View key={`ellipsis-${index}`} style={$ellipsisContainer}>
                <Text style={$ellipsisText}>...</Text>
              </View>
            )
          }

          const pageNumber = page as number
          const isActive = pageNumber === currentPage

          return (
            <Pressable
              key={pageNumber}
              onPress={() => !isActive && onPageChange(pageNumber)}
              disabled={isActive}
              style={({ pressed }) => [
                $pageButton,
                isActive && $pageButtonActive,
                pressed && !isActive && $pageButtonPressed,
              ]}
            >
              <Text style={[$pageButtonText, isActive && $pageButtonTextActive]}>{pageNumber}</Text>
            </Pressable>
          )
        })}
      </View>

      {/* Next Button */}
      <Pressable
        onPress={() => canGoNext && onPageChange(currentPage + 1)}
        disabled={!canGoNext}
        style={({ pressed }) => [
          $navigationButton,
          !canGoNext && $navigationButtonDisabled,
          pressed && canGoNext && $navigationButtonPressed,
        ]}
      >
        <Text style={[$navigationButtonText, !canGoNext && $navigationButtonTextDisabled]}>
          Weiter
        </Text>
      </Pressable>
    </View>
  )
}

// Container Styles
const $container: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  paddingVertical: spacing.lg,
  gap: spacing.xs,
}

const $pagesContainer: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  gap: spacing.xxs,
}

// Navigation Button Styles
const $navigationButton: ViewStyle = {
  paddingHorizontal: spacing.md,
  paddingVertical: spacing.sm,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: colors.border,
  backgroundColor: colors.surface,
  minWidth: 80,
  alignItems: "center",
}

const $navigationButtonDisabled: ViewStyle = {
  opacity: 0.4,
  borderColor: colors.palette.neutral300,
}

const $navigationButtonPressed: ViewStyle = {
  backgroundColor: colors.palette.neutral200,
}

const $navigationButtonText: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.text,
}

const $navigationButtonTextDisabled: TextStyle = {
  color: colors.textDim,
}

// Page Button Styles
const $pageButton: ViewStyle = {
  width: 40,
  height: 40,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: colors.border,
  backgroundColor: colors.surface,
  alignItems: "center",
  justifyContent: "center",
}

const $pageButtonActive: ViewStyle = {
  backgroundColor: colors.primary,
  borderColor: colors.primary,
}

const $pageButtonPressed: ViewStyle = {
  backgroundColor: colors.palette.neutral200,
}

const $pageButtonText: TextStyle = {
  fontSize: 14,
  fontWeight: "500",
  color: colors.text,
}

const $pageButtonTextActive: TextStyle = {
  color: colors.palette.neutral100,
  fontWeight: "600",
}

// Ellipsis Styles
const $ellipsisContainer: ViewStyle = {
  width: 40,
  height: 40,
  alignItems: "center",
  justifyContent: "center",
}

const $ellipsisText: TextStyle = {
  fontSize: 16,
  color: colors.textDim,
  fontWeight: "500",
}
